import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function MonthlyAttendanceEntry({ userClass, students, onClose }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [workingDays, setWorkingDays] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [quickPresent, setQuickPresent] = useState('');
  const [quickAbsent, setQuickAbsent] = useState('');
  const [lastAppliedValues, setLastAppliedValues] = useState({ present: '', absent: '', studentId: '' });
  const [manuallyModified, setManuallyModified] = useState(new Set());

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    // For 2025-26 session: Allow May 1-15 and June 26-31
    const currentYear = new Date().getFullYear();
    const isSession2025_26 = currentYear === 2025;
    
    if (isSession2025_26) {
      // Allow May and June with specific date restrictions
      fetchWorkingDays();
      fetchExistingAttendance();
    } else {
      // Default behavior: Don't fetch working days during summer vacation
      if (selectedMonth === 5 || selectedMonth === 6) {
        setWorkingDays([]);
        setAttendanceData({});
        setLoading(false);
        return;
      }
      fetchWorkingDays();
      fetchExistingAttendance();
    }
  }, [selectedMonth, selectedYear]);

  const fetchWorkingDays = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/working-days/${userClass}/${selectedYear}/${selectedMonth}`);
      setWorkingDays(response.data || []);
    } catch (error) {
      console.error('Error fetching working days:', error);
      setWorkingDays([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAttendance = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/attendance-records/${userClass}`);
      const records = response.data || [];
      
      const monthRecords = records.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate.getMonth() + 1 === selectedMonth && recordDate.getFullYear() === selectedYear;
      });

      const organized = {};
      students.forEach(student => {
        organized[student._id] = {};
        monthRecords
          .filter(record => record.studentId === student._id)
          .forEach(record => {
            organized[student._id][record.date] = record.status;
          });
      });

      setAttendanceData(organized);
    } catch (error) {
      console.error('Error fetching existing attendance:', error);
      setAttendanceData({});
    }
  };

  const setStudentAttendance = (studentId, presentDays, absentDays) => {
    const newAttendance = {};
    const totalDays = presentDays + absentDays;
    const todayIST = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000));
    const today = todayIST.toISOString().split('T')[0];
    
    // Filter working days to only include past and current dates
    const validDays = workingDays.filter(date => date <= today);
    
    if (totalDays > validDays.length) {
      alert(`Total days (${totalDays}) cannot exceed available days up to today (${validDays.length})`);
      return;
    }
    
    // Create array of indices and shuffle for random distribution
    const dayIndices = [...Array(validDays.length).keys()];
    const shuffledIndices = dayIndices.sort(() => Math.random() - 0.5);
    
    // Assign present days randomly
    const presentIndices = shuffledIndices.slice(0, presentDays);
    const absentIndices = shuffledIndices.slice(presentDays, totalDays);
    
    presentIndices.forEach(index => {
      newAttendance[validDays[index]] = 'present';
    });
    
    absentIndices.forEach(index => {
      newAttendance[validDays[index]] = 'absent';
    });

    setAttendanceData(prev => ({
      ...prev,
      [studentId]: newAttendance
    }));
    
    // Remove from manually modified set since this is bulk entry
    setManuallyModified(prev => {
      const newSet = new Set(prev);
      newSet.delete(studentId);
      return newSet;
    });
  };

  const setStudentDayAttendance = (studentId, date, status) => {
    const todayIST = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000));
    const today = todayIST.toISOString().split('T')[0];
    
    if (date > today) {
      alert('Cannot mark attendance for future dates');
      return;
    }
    
    setAttendanceData(prev => {
      const newData = {
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [date]: status
        }
      };
      
      // Update input fields immediately with new data
      if (selectedStudent === studentId) {
        const studentAttendance = newData[studentId] || {};
        const present = Object.values(studentAttendance).filter(s => s === 'present').length;
        const absent = Object.values(studentAttendance).filter(s => s === 'absent').length;
        setQuickPresent(present.toString());
        setQuickAbsent(absent.toString());
      }
      
      return newData;
    });
    
    // Mark student as manually modified
    setManuallyModified(prev => new Set([...prev, studentId]));
  };

  const saveStudentAttendance = async (studentId) => {
    try {
      setSaving(true);
      const promises = [];
      const studentData = attendanceData[studentId] || {};

      Object.keys(studentData).forEach(date => {
        const status = studentData[date];
        if (status) {
          promises.push(
            axios.post(`${API_BASE_URL}/api/attendance-records`, {
              studentId,
              class: userClass,
              date,
              status,
              markedBy: 'Teacher (Individual Save)'
            })
          );
        }
      });

      await Promise.all(promises);
      const studentName = students.find(s => s._id === studentId)?.name;
      alert(`Attendance saved for ${studentName}!`);
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('Error saving attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveAllAttendance = async () => {
    try {
      setSaving(true);
      const promises = [];

      Object.keys(attendanceData).forEach(studentId => {
        Object.keys(attendanceData[studentId]).forEach(date => {
          const status = attendanceData[studentId][date];
          if (status) {
            promises.push(
              axios.post(`${API_BASE_URL}/api/attendance-records`, {
                studentId,
                class: userClass,
                date,
                status,
                markedBy: 'Teacher (Monthly Entry)'
              })
            );
          }
        });
      });

      await Promise.all(promises);
      alert('Monthly attendance saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('Error saving attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetStudentAttendance = async (studentId, studentName) => {
    const confirmReset = window.confirm(
      `⚠️ RESET ATTENDANCE DATA\n\n` +
      `This will permanently delete ALL attendance records for:\n` +
      `Student: ${studentName}\n` +
      `Month: ${monthNames[selectedMonth - 1]} ${selectedYear}\n\n` +
      `This action cannot be undone. Are you sure you want to continue?`
    );
    
    if (!confirmReset) return;
    
    try {
      setSaving(true);
      const response = await axios.delete(
        `${API_BASE_URL}/api/attendance-records/${studentId}/${selectedYear}/${selectedMonth}`,
        {
          data: { deletedBy: 'Teacher (Monthly Entry Reset)' }
        }
      );
      
      if (response.data.success) {
        // Clear the local state for this student
        setAttendanceData(prev => ({ ...prev, [studentId]: {} }));
        setManuallyModified(prev => {
          const newSet = new Set(prev);
          newSet.delete(studentId);
          return newSet;
        });
        
        alert(
          `✅ ATTENDANCE RESET SUCCESSFUL\n\n` +
          `${response.data.deletedCount} attendance records deleted for ${studentName}\n` +
          `Month: ${monthNames[selectedMonth - 1]} ${selectedYear}`
        );
        
        // Refresh the attendance data
        fetchExistingAttendance();
      }
    } catch (error) {
      console.error('Error resetting student attendance:', error);
      alert(
        `❌ RESET FAILED\n\n` +
        `Could not reset attendance for ${studentName}.\n` +
        `Error: ${error.response?.data?.message || error.message}\n\n` +
        `Please try again or contact support.`
      );
    } finally {
      setSaving(false);
    }
  };



  const getStudentStats = (studentId) => {
    const studentAttendance = attendanceData[studentId] || {};
    const present = Object.values(studentAttendance).filter(status => status === 'present').length;
    const absent = Object.values(studentAttendance).filter(status => status === 'absent').length;
    const total = present + absent;
    const percentage = total > 0 ? ((present / total) * 100).toFixed(1) : 0;
    return { present, absent, total, percentage };
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.8)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start', 
      zIndex: 9999, 
      overflow: 'auto',
      padding: window.innerWidth <= 768 ? '10px 5px' : '20px 0'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: window.innerWidth <= 768 ? '10px' : '15px', 
        width: window.innerWidth <= 768 ? '100%' : '95%', 
        maxWidth: '1200px', 
        maxHeight: window.innerWidth <= 768 ? '95vh' : '90vh', 
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ 
          padding: window.innerWidth <= 768 ? '15px 10px' : '20px', 
          borderBottom: '2px solid #3b82f6', 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderRadius: window.innerWidth <= 768 ? '10px 10px 0 0' : '15px 15px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, color: '#1565c0', fontSize: window.innerWidth <= 768 ? '18px' : '24px', fontWeight: 'bold' }}>
                📅 Monthly Entry - Class {userClass}
              </h2>
              <p style={{ margin: '5px 0 0 0', color: '#1976d2', fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                {monthNames[selectedMonth - 1]} {selectedYear}
              </p>
            </div>
            <button 
              onClick={onClose}
              style={{ 
                background: '#dc2626', 
                color: 'white', 
                border: 'none', 
                padding: '10px 15px', 
                borderRadius: '50%', 
                cursor: 'pointer',
                fontSize: '18px',
                width: '45px',
                height: '45px'
              }}
            >
              ✕
            </button>
          </div>

          {/* Month/Year Selection */}
          <div style={{ display: 'flex', gap: window.innerWidth <= 768 ? '8px' : '15px', marginTop: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Month:</label>
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Year:</label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              >
                {[2023, 2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.8)', 
              padding: window.innerWidth <= 768 ? '6px 8px' : '8px 12px', 
              borderRadius: '8px',
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              fontWeight: 'bold',
              color: '#1565c0'
            }}>
              Days: {workingDays.length}
            </div>
          </div>
        </div>

        {(() => {
          const currentYear = new Date().getFullYear();
          const isSession2025_26 = currentYear === 2025;
          const isSummerVacationMonth = !isSession2025_26 && (selectedMonth === 5 || selectedMonth === 6);
          
          if (isSummerVacationMonth) {
            return (
              <div style={{ 
                padding: '50px', 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                color: 'white',
                borderRadius: '15px',
                margin: '20px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏖️</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Summer Vacation</div>
                <div style={{ fontSize: '16px', opacity: 0.9 }}>
                  {selectedMonth === 5 ? 'May 16-31: No attendance required' : 'June 1-30: No attendance required'}
                </div>
                <div style={{ fontSize: '14px', marginTop: '15px', opacity: 0.8 }}>
                  Enjoy your summer break! Classes will resume after June 30th.
                </div>
              </div>
            );
          }
          
          if (isSession2025_26 && (selectedMonth === 5 || selectedMonth === 6)) {
            return (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                borderRadius: '15px',
                margin: '20px'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '15px' }}>📚</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>Session 2025-26 Special Schedule</div>
                <div style={{ fontSize: '16px', opacity: 0.9 }}>
                  {selectedMonth === 5 ? 'May 1-15: Classes & Attendance Required' : 'June 26-31: Classes & Attendance Required'}
                </div>
                <div style={{ fontSize: '14px', marginTop: '15px', opacity: 0.8 }}>
                  {selectedMonth === 5 ? 'Summer vacation starts May 16th' : 'Classes resume June 26th after summer break'}
                </div>
              </div>
            );
          }
          
          return null;
        })()} 
        
        {loading ? (
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px', color: '#666' }}>Loading working days...</div>
          </div>
        ) : (
          <div style={{ padding: window.innerWidth <= 768 ? '10px' : '20px' }}>
            {/* Quick Student Selector */}
            <div style={{ background: '#e3f2fd', padding: window.innerWidth <= 768 ? '15px 10px' : '20px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #2196f3' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#1565c0' }}>🎯 Quick Student Entry</h3>
              <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 1fr auto', gap: window.innerWidth <= 768 ? '10px' : '15px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#1565c0' }}>Select Student:</label>
                  <select 
                    value={selectedStudent} 
                    onChange={(e) => {
                      const studentId = e.target.value;
                      setSelectedStudent(studentId);
                      if (studentId) {
                        const stats = getStudentStats(studentId);
                        setQuickPresent(stats.present.toString());
                        setQuickAbsent(stats.absent.toString());
                      } else {
                        setQuickPresent('');
                        setQuickAbsent('');
                      }
                    }}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                  >
                    <option value="">Choose a student...</option>
                    {students.map(student => (
                      <option key={student._id} value={student._id}>{student.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#4caf50' }}>Present Days:</label>
                  <input 
                    type="number" 
                    value={quickPresent}
                    onChange={(e) => setQuickPresent(e.target.value)}
                    placeholder="0"
                    min="0" 
                    max={workingDays.filter(date => date <= new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString().split('T')[0]).length}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#f44336' }}>Absent Days:</label>
                  <input 
                    type="number" 
                    value={quickAbsent}
                    onChange={(e) => setQuickAbsent(e.target.value)}
                    placeholder="0"
                    min="0" 
                    max={workingDays.filter(date => date <= new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString().split('T')[0]).length}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: window.innerWidth <= 768 ? 'stretch' : 'flex-start' }}>
                  <button 
                    onClick={() => {
                      if (selectedStudent) {
                        const present = parseInt(quickPresent) || 0;
                        const absent = parseInt(quickAbsent) || 0;
                        
                        // Check if student has been manually modified
                        if (manuallyModified.has(selectedStudent)) {
                          alert('This student\'s attendance has been manually modified day-by-day. Randomization is only available when using bulk Present/Absent entry at the top.');
                          return;
                        }
                        
                        // Check if values changed since last apply
                        const valuesChanged = 
                          lastAppliedValues.present !== quickPresent ||
                          lastAppliedValues.absent !== quickAbsent ||
                          lastAppliedValues.studentId !== selectedStudent;
                        
                        if (valuesChanged) {
                          setStudentAttendance(selectedStudent, present, absent);
                          setLastAppliedValues({ present: quickPresent, absent: quickAbsent, studentId: selectedStudent });
                        }
                      }
                    }}
                    disabled={!selectedStudent}
                    style={{ 
                      background: selectedStudent ? (manuallyModified.has(selectedStudent) ? '#ff9800' : '#2196f3') : '#ccc', 
                      color: 'white', 
                      border: 'none', 
                      padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px', 
                      borderRadius: '5px', 
                      cursor: selectedStudent ? 'pointer' : 'not-allowed',
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                      fontWeight: 'bold',
                      flex: window.innerWidth <= 768 ? '1' : 'none'
                    }}
                  >
                    {selectedStudent && manuallyModified.has(selectedStudent) ? '⚠️ Manual' : '✅ Apply'}
                  </button>
                  <button 
                    onClick={() => {
                      if (selectedStudent) {
                        saveStudentAttendance(selectedStudent);
                      }
                    }}
                    disabled={!selectedStudent}
                    style={{ 
                      background: selectedStudent ? '#10b981' : '#ccc', 
                      color: 'white', 
                      border: 'none', 
                      padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px', 
                      borderRadius: '5px', 
                      cursor: selectedStudent ? 'pointer' : 'not-allowed',
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                      fontWeight: 'bold',
                      flex: window.innerWidth <= 768 ? '1' : 'none'
                    }}
                  >
                    💾 Save
                  </button>
                </div>
              </div>
              <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                💡 Apply: Randomize distribution (only for bulk entry) | Save: Save current student | Available days: {workingDays.filter(date => date <= new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString().split('T')[0]).length}/{workingDays.length}
              </p>
            </div>

            {/* Individual Day Marking for Selected Student */}
            {selectedStudent && (
              <div style={{ background: '#f0f8ff', padding: window.innerWidth <= 768 ? '10px' : '15px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #3b82f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: '0', color: '#1565c0' }}>
                    📅 Day-by-Day for {students.find(s => s._id === selectedStudent)?.name}
                  </h4>
                  <div style={{ display: 'flex', gap: '15px', fontSize: '14px', fontWeight: 'bold' }}>
                    <span style={{ color: '#4caf50' }}>Present: {getStudentStats(selectedStudent).present}</span>
                    <span style={{ color: '#f44336' }}>Absent: {getStudentStats(selectedStudent).absent}</span>
                    <span style={{ color: '#2196f3' }}>Total: {getStudentStats(selectedStudent).total}/{workingDays.filter(date => date <= new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString().split('T')[0]).length}</span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(auto-fill, minmax(80px, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))', gap: window.innerWidth <= 768 ? '5px' : '8px', maxHeight: window.innerWidth <= 768 ? '150px' : '200px', overflowY: 'auto' }}>
                  {(() => {
                    const todayIST = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000));
                    const today = todayIST.toISOString().split('T')[0];
                    const isCurrentMonth = todayIST.getMonth() + 1 === selectedMonth && todayIST.getFullYear() === selectedYear;
                    const isTodayWorkingDay = todayIST.getDay() !== 0;
                    
                    let allDays = [...workingDays];
                    if (isCurrentMonth && isTodayWorkingDay && !workingDays.includes(today)) {
                      allDays.push(today);
                      allDays.sort();
                    }
                    
                    return allDays.map(date => {
                      const dateObj = new Date(date);
                      const status = attendanceData[selectedStudent]?.[date];
                      const isFuture = date > today;
                      return (
                        <div key={date} style={{ 
                          border: '1px solid #ddd', 
                          borderRadius: '5px', 
                          padding: window.innerWidth <= 768 ? '5px' : '8px', 
                          background: isFuture ? '#f5f5f5' : 'white',
                          opacity: isFuture ? 0.5 : 1
                        }}>
                        <div style={{ fontSize: window.innerWidth <= 768 ? '10px' : '12px', fontWeight: 'bold', marginBottom: window.innerWidth <= 768 ? '3px' : '5px' }}>
                          {dateObj.getDate()}/{dateObj.getMonth() + 1}
                          {isFuture && window.innerWidth > 768 && <span style={{ color: '#999', fontSize: '8px' }}> (Future)</span>}
                        </div>
                        <div style={{ display: 'flex', gap: window.innerWidth <= 768 ? '2px' : '3px' }}>
                          <button 
                            onClick={() => setStudentDayAttendance(selectedStudent, date, 'present')}
                            disabled={isFuture}
                            style={{ 
                              flex: 1, 
                              padding: window.innerWidth <= 768 ? '2px' : '3px', 
                              fontSize: window.innerWidth <= 768 ? '8px' : '10px', 
                              border: status === 'present' ? '2px solid #4caf50' : '1px solid #ddd',
                              background: status === 'present' ? '#e8f5e8' : 'white',
                              color: status === 'present' ? '#2e7d32' : '#666',
                              borderRadius: '3px',
                              cursor: isFuture ? 'not-allowed' : 'pointer',
                              opacity: isFuture ? 0.5 : 1
                            }}
                          >
                            P
                          </button>
                          <button 
                            onClick={() => setStudentDayAttendance(selectedStudent, date, 'absent')}
                            disabled={isFuture}
                            style={{ 
                              flex: 1, 
                              padding: window.innerWidth <= 768 ? '2px' : '3px', 
                              fontSize: window.innerWidth <= 768 ? '8px' : '10px', 
                              border: status === 'absent' ? '2px solid #f44336' : '1px solid #ddd',
                              background: status === 'absent' ? '#ffebee' : 'white',
                              color: status === 'absent' ? '#c62828' : '#666',
                              borderRadius: '3px',
                              cursor: isFuture ? 'not-allowed' : 'pointer',
                              opacity: isFuture ? 0.5 : 1
                            }}
                          >
                            A
                          </button>
                          <button 
                            onClick={() => {
                              setAttendanceData(prev => {
                                const newData = { ...prev };
                                if (newData[selectedStudent]) {
                                  const updatedStudent = { ...newData[selectedStudent] };
                                  delete updatedStudent[date];
                                  newData[selectedStudent] = updatedStudent;
                                }
                                return newData;
                              });
                              
                              // Update input fields immediately
                              const updatedStats = getStudentStats(selectedStudent);
                              setQuickPresent(updatedStats.present.toString());
                              setQuickAbsent(updatedStats.absent.toString());
                              
                              // Mark as manually modified
                              setManuallyModified(prev => new Set([...prev, selectedStudent]));
                            }}
                            disabled={isFuture}
                            style={{ 
                              flex: 1, 
                              padding: window.innerWidth <= 768 ? '2px' : '3px', 
                              fontSize: window.innerWidth <= 768 ? '8px' : '10px', 
                              border: '1px solid #ddd',
                              background: !status ? '#f5f5f5' : 'white',
                              color: '#666',
                              borderRadius: '3px',
                              cursor: isFuture ? 'not-allowed' : 'pointer',
                              opacity: isFuture ? 0.5 : 1
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                    });
                  })()
                  }
                </div>
              </div>
            )}

            {/* Students Summary Table */}
            <div style={{ overflowX: 'auto', marginTop: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: window.innerWidth <= 768 ? '12px' : '14px', minWidth: window.innerWidth <= 768 ? '500px' : 'auto' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
                    <th style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'left' }}>Student Name</th>
                    <th style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center' }}>Present</th>
                    <th style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center' }}>Absent</th>
                    <th style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center' }}>%</th>
                    <th style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => {
                    const stats = getStudentStats(student._id);
                    return (
                      <tr key={student._id} style={{ 
                        background: selectedStudent === student._id ? '#e3f2fd' : (index % 2 === 0 ? '#f8f9fa' : 'white'),
                        borderBottom: '1px solid #eee'
                      }}>
                        <td style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                          {student.name}
                        </td>
                        <td style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center', background: stats.present > 0 ? '#e8f5e8' : 'transparent', fontWeight: 'bold', color: stats.present > 0 ? '#2e7d32' : '#666' }}>
                          {stats.present}
                        </td>
                        <td style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center', background: stats.absent > 0 ? '#ffebee' : 'transparent', fontWeight: 'bold', color: stats.absent > 0 ? '#c62828' : '#666' }}>
                          {stats.absent}
                        </td>
                        <td style={{ padding: window.innerWidth <= 768 ? '8px 5px' : '10px 8px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: stats.percentage >= 75 ? '#2e7d32' : stats.percentage >= 50 ? '#f57c00' : '#c62828' }}>
                          {stats.percentage}%
                        </td>
                        <td style={{ padding: window.innerWidth <= 768 ? '4px' : '6px', border: '1px solid #ddd', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: window.innerWidth <= 768 ? '2px' : '3px', justifyContent: 'center', flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap' }}>
                            <button onClick={() => setSelectedStudent(student._id)} style={{ background: '#2196f3', color: 'white', border: 'none', padding: window.innerWidth <= 768 ? '2px 4px' : '3px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: window.innerWidth <= 768 ? '8px' : '10px' }}>Edit</button>
                            <button onClick={() => setStudentAttendance(student._id, workingDays.filter(date => date <= new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString().split('T')[0]).length, 0)} style={{ background: '#4caf50', color: 'white', border: 'none', padding: window.innerWidth <= 768 ? '2px 4px' : '3px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: window.innerWidth <= 768 ? '8px' : '10px' }}>All P</button>
                            <button onClick={() => {
                              setAttendanceData(prev => ({ ...prev, [student._id]: {} }));
                              setManuallyModified(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(student._id);
                                return newSet;
                              });
                            }} style={{ background: '#f44336', color: 'white', border: 'none', padding: window.innerWidth <= 768 ? '2px 4px' : '3px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: window.innerWidth <= 768 ? '8px' : '10px' }}>Clear</button>
                            <button onClick={() => resetStudentAttendance(student._id, student.name)} style={{ background: '#dc2626', color: 'white', border: 'none', padding: window.innerWidth <= 768 ? '2px 4px' : '3px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: window.innerWidth <= 768 ? '8px' : '10px' }}>🗑️ Reset</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Instructions */}
            <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '15px', marginTop: '20px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>💡 How to use:</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404' }}>
                <li><strong>Random Distribution:</strong> Present/Absent days are assigned randomly across the month</li>
                <li><strong>Real-time Sync:</strong> Individual day changes update the counts above automatically</li>
                <li><strong>Edit Button:</strong> Click to select student and see current attendance data</li>
                <li><strong>All P:</strong> Mark all working days as present</li>
                <li><strong>Clear:</strong> Remove all attendance for that student</li>
              </ul>
            </div>



            {/* Save Button */}
            <div style={{ textAlign: 'center', marginTop: window.innerWidth <= 768 ? '20px' : '30px', paddingTop: window.innerWidth <= 768 ? '15px' : '20px', borderTop: '1px solid #ddd' }}>
              <button 
                onClick={saveAllAttendance}
                disabled={saving}
                style={{ 
                  background: saving ? '#6c757d' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                  color: 'white', 
                  border: 'none', 
                  padding: window.innerWidth <= 768 ? '12px 20px' : '15px 40px', 
                  borderRadius: '10px', 
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: window.innerWidth <= 768 ? '14px' : '18px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                  width: window.innerWidth <= 768 ? '100%' : 'auto'
                }}
              >
                {saving ? '💾 Saving...' : window.innerWidth <= 768 ? '💾 Save All' : '💾 Save Monthly Attendance'}
              </button>
              <p style={{ marginTop: '10px', color: '#666', fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
                {window.innerWidth <= 768 ? 'Save all student attendance' : 'This will save attendance for all students with entered data'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonthlyAttendanceEntry;